import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  BaseSite,
  BaseSiteService,
  JavaRegExpConverter,
  SiteContextConfig,
  SiteContextConfigInitializer,
  WindowRef,
} from '@spartacus/core';
import { Observable, forkJoin, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CustomOccSitesConfigLoader } from 'src/app/core/site-context/custom-config-loader';
import { SiteContextService } from '../site-context/site-context.service';
import { LoginService } from '../user/login.service';

@Injectable({
  providedIn: 'root',
})
export class ShimanoOccConfigLoaderService extends SiteContextConfigInitializer {
  constructor(
    protected baseSiteService: BaseSiteService,
    protected javaRegExpConverter: JavaRegExpConverter,
    protected winRef: WindowRef,
    protected customSitesConfigLoader: CustomOccSitesConfigLoader,
    private loginService: LoginService,
    private siteContextService?: SiteContextService,
    private location?: Location
  ) {
    super(baseSiteService, javaRegExpConverter, winRef);
  }

  private get shmanoCurrentUrl(): string {
    return this.winRef.location.href as string;
  }

  protected resolveConfig(): Observable<SiteContextConfig> {
    return this.baseSiteService.getAll().pipe(
      map((baseSites) => baseSites?.find((site) => this.isShimanoCurrentBaseSite(site))),
      filter((baseSite: any) => {
        if (!baseSite) {
          throw new Error(
            `Error: Cannot get base site config! Current url (${this.shmanoCurrentUrl}) doesn't match any of url patterns of any base sites.`
          );
        }
        return Boolean(baseSite);
      }),
      switchMap((baseSite) => {
        let contexts = this.location.path().split('/');
        let urlEncodingAttributes = ['shimanoBusinessType', 'shimanoCountry', 'shimanoLanguage'];
        let newBaseSite = { urlEncodingAttributes, ...baseSite };

        return forkJoin([
          of(newBaseSite),
          this.siteContextService?.getShimanoSiteContexts$(contexts[1], contexts[2], contexts[3]),
        ]);
      }),
      map(([baseSite, shimanoSiteContext]) => {
        //  For /oauth/authorize redirect uri
        const logoutContextRaw = sessionStorage.getItem('logoutContext');
        const logoutContext = logoutContextRaw ? JSON.parse(logoutContextRaw) : null;
        if (logoutContext) {
          this.loginService.setOAuthRedirectUri(
            logoutContext.businessType,
            logoutContext.country,
            logoutContext.language
          );
          sessionStorage.removeItem('logoutContext');
        } else {
          this.loginService.setOAuthRedirectUri(
            shimanoSiteContext?.businessTypes?.[0],
            shimanoSiteContext?.countries?.[0],
            shimanoSiteContext?.languages?.[0]
          );
        }
        return this.getShimanoConfig(baseSite, shimanoSiteContext);
      }),
      take(1)
    );
  }

  protected getShimanoConfig(source: BaseSite, shimanoSiteContext: any): SiteContextConfig {
    // get the site context config
    const siteContextConfig = super.getConfig(source);

    // set custom ShimanoSiteContextConfig
    siteContextConfig.context.baseSite = shimanoSiteContext.baseSites;
    siteContextConfig.context.shimanoBusinessType = shimanoSiteContext.businessTypes;
    siteContextConfig.context.shimanoCountry = shimanoSiteContext.countries;
    siteContextConfig.context.shimanoLanguage = shimanoSiteContext.languages;
    siteContextConfig.context.language = shimanoSiteContext.languages;

    const config = {
      context: { ...siteContextConfig.context },
    };
    return config;
  }

  private isShimanoCurrentBaseSite(site: BaseSite): boolean {
    const index = (site.urlPatterns || []).findIndex((javaRegexp: string) => {
      const jsRegexp = this.javaRegExpConverter.toJsRegExp(javaRegexp);
      if (jsRegexp) {
        const result = jsRegexp.test(this.shmanoCurrentUrl);
        return result;
      }
    });

    return index !== -1;
  }
}
